<script setup lang="ts">
import { UserInProjectType } from "@/features/BuildingProject";
import { ProjectBaseFragment } from "@/graphql";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import {
  getBuildingProjectUserList,
  inviteUserToBuildingProject,
  uninviteUserFromBuildingProject,
  updateUserRole,
} from "@/api/services/BuildingProject";
import { getUsersByTenantId } from "@/api/services/Frontegg";
import usePermissions from "@/providers/Permissions/PermissionsProvider";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import User from "@/types/auth/User";
import Button from "@/components/Reusables/Buttons/Button.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import Table from "@/components/Reusables/Table/Table.vue";
import type { Column } from "@/components/Reusables/Table/types";
import SearchSelectUser from "./SearchSelectUser.vue";
import MemberIdentity from "./MemberIdentity.vue";
import MemberRoleMenu from "./MemberRoleMenu.vue";
import MemberSpeckleFlag from "./MemberSpeckleFlag.vue";
import { useMemberSpeckleStatus, speckleInviteWarning } from "./useMemberSpeckleStatus.composable";
import { Role } from "@/providers/Permissions/PermissionsTypes";

/**
 * Who has access to a project, and the invite / role / removal controls for changing that.
 *
 * Extracted out of `InviteModalNew` unchanged, so the same member list can render in two shapes: as
 * that dialog (opened from the project list and the old nav) and INLINE on the project's settings
 * page, where the members are simply on the page rather than one click behind a button. This file
 * owns all of the behaviour; the modal is now only a shell around it.
 *
 * It has no heading of its own: each host titles it. The dialog puts the project's name in its
 * chrome, and the settings card has a "Members" band above it.
 */
const props = defineProps<{
  project: ProjectBaseFragment;
  /**
   * Show the membership without offering to change it — no invite row, and every role renders as
   * plain text. Used while previewing a frozen revision: memberships are not revision-scoped, so a
   * write from there would land on the live project while the reader looks at history.
   */
  readOnly?: boolean;
}>();

const authState = useAuthStateStore();
const { user } = storeToRefs(authState);
const usersAlreadyInProject = ref<Array<UserInProjectType>>([]);
const allUsers = ref<Array<User>>([]);
const users = ref<Array<User>>([]);
const selectedUser = ref<User>();
const roles = ref<Array<string>>(["Admin", "Contributor", "Viewer"]);
const selectedRole = ref<string | null>(null);
const isInviting = ref<boolean>(false);
const userListLoaded = ref<boolean>(false);
const tenantId = ref<string>();

const selfIsContributor = ref<boolean>(false);
const selfIsAdmin = ref<boolean>(false);

// The member list starts out loading rather than empty. Without this the list rendered
// "0 members" over an empty box for as long as the fetch took (measured ~1.4s), stating something
// false before correcting itself. Only ever set false, so the refetch after a removal doesn't blank
// the list out again.
const isLoadingMembers = ref<boolean>(true);
const membersError = ref<boolean>(false);

// Members with a role change or removal in flight. A Set because it's keyed by user id; Vue tracks
// add/delete on reactive collections, so `.has()` in the template stays reactive.
const pendingUserIds = ref<Set<string>>(new Set());

const memberSpeckleStatus = useMemberSpeckleStatus(
  () => props.project.speckleStreamId,
  usersAlreadyInProject,
  () => user.value?.userId
);

onMounted(async () => {
  tenantId.value = user.value?.tenantId;
  if (tenantId.value === undefined) {
    // Nothing to fetch without a tenant, so stop showing the loading rows instead of spinning forever.
    isLoadingMembers.value = false;
    membersError.value = true;
    return;
  }

  const projectRole = props.project.role;

  if (projectRole) {
    if (usePermissions().isAdminRole(projectRole as Role)) {
      selfIsAdmin.value = true;
    } else if (usePermissions().canEdit([projectRole as Role])) {
      selfIsContributor.value = true;
    }
  }

  if (!selfIsAdmin.value) {
    roles.value.splice(0, 1);
  }

  initializeUsers(tenantId.value);
});

const initializeUsers = async (tenantId: string) => {
  membersError.value = false;
  try {
    const response = await getUsersByTenantId(tenantId);
    allUsers.value = JSON.parse(JSON.stringify(response.items));
    users.value = response.items.sort((a: User, b: User) => (a.name > b.name ? 1 : -1));
    usersAlreadyInProject.value = await getBuildingProjectUserList(props.project.id);

    usersAlreadyInProject.value.map((userAlreadyInProject) => {
      userAlreadyInProject.dropdownIsShown = false;
    });

    removeUsersFromListIfAlreadyPartOfProject();
    if (props.project.speckleStreamId) void memberSpeckleStatus.refresh();
  } catch (error) {
    // Previously unhandled: a failure here rejected silently, leaving "0 members" on screen and the
    // user-search input spinning forever with nothing said about why.
    membersError.value = true;
    Sentry.captureException(error);
    useToast().error("Could not load this project's members");
  } finally {
    isLoadingMembers.value = false;
    // Also released on failure so the search field doesn't sit disabled behind a spinner; it then
    // shows its own "No users found" state instead.
    userListLoaded.value = true;
  }
};

const removeUsersFromListIfAlreadyPartOfProject = () => {
  usersAlreadyInProject.value.forEach((userAlreadyInProject) => {
    if (!users.value.find((usr) => usr.email === userAlreadyInProject.email)) return;

    users.value.splice(
      users.value.findIndex((usr) => usr.email === userAlreadyInProject.email),
      1
    );
  });
  userListLoaded.value = true;
};

const inviteUser = async () => {
  if (!selectedUser.value || !selectedRole.value) {
    if (!selectedUser.value && !selectedRole.value) useToast().warning("Please select a user and a role");
    if (!selectedUser.value && selectedRole.value) useToast().warning("Please select a user");
    if (!selectedRole.value && selectedUser.value) useToast().warning("Please select a role");
    return;
  }

  isInviting.value = true;

  const roleId =
    selectedRole.value === "Admin"
      ? "project.admin"
      : selectedRole.value === "Contributor"
        ? "project.contributor"
        : "project.viewer";

  const req = {
    inviteeUserId: selectedUser.value.id,
    inviteeEmail: selectedUser.value.email,
    roleId,
  };

  await inviteUserToBuildingProject(req, props.project.id)
    .then((response) => {
      if (response !== undefined && selectedUser.value) {
        isInviting.value = false;
        useToast().success(`${selectedUser.value?.name} was invited`);
        const invitee: UserInProjectType = {
          email: selectedUser.value.email,
          name: selectedUser.value.name,
          role: selectedRole.value?.toLowerCase() ?? "",
          userId: selectedUser.value.id,
          dropdownIsShown: false,
          speckleStatus: null,
        };
        usersAlreadyInProject.value.push(invitee);
        // Invited, but not onto the project's Speckle model. Kept up until dismissed, as the role change's is.
        void memberSpeckleStatus.checkOne(invitee).then((status) => {
          const warning = speckleInviteWarning(status);
          if (warning) useToast().warning(`${invitee.name}: ${warning}`, { timeout: false });
        });

        users.value.splice(
          users.value.findIndex((usr) => usr.id === selectedUser.value?.id),
          1
        );
        selectedUser.value = undefined;
        selectedRole.value = null;
      }
    })
    .catch(() => {
      isInviting.value = false;
      useToast().error(`Something went wrong with while trying to invite ${selectedUser.value?.name}`);
    });
};

// `undefined` when the search field has been edited away from the picked user: the selection has to
// be retractable, or the Invite button stays enabled over a field the reader sees as empty.
const handleSelectedUser = (usr: User | undefined) => {
  selectedUser.value = usr;
};

const handleRemoveUser = async (user: UserInProjectType) => {
  pendingUserIds.value.add(user.userId);
  try {
    await uninviteUserFromBuildingProject(props.project.id, user.userId);

    usersAlreadyInProject.value.splice(
      usersAlreadyInProject.value.findIndex((userAlreadyInProject) => userAlreadyInProject.userId === user.userId),
      1
    );

    useToast().warning(`Successfully removed ${user.name} from the project`);
    if (tenantId.value) await initializeUsers(tenantId.value);
  } catch (error) {
    // Previously unhandled: a failed removal rejected silently, so the row stayed put with no
    // indication that anything had gone wrong.
    Sentry.captureException(error);
    useToast().error(`Could not remove ${user.name} from the project`);
  } finally {
    pendingUserIds.value.delete(user.userId);
  }
};

const handleChangeRole = async (user: UserInProjectType, newRole: string) => {
  const roleId = newRole === "admin" ? "project.admin" : newRole === "contributor" ? "project.contributor" : "project.viewer";

  // The row shows a spinner while this is in flight. `user.role` is still only assigned on success,
  // so the visible role never claims something the server hasn't confirmed.
  pendingUserIds.value.add(user.userId);
  try {
    const response = await updateUserRole(props.project.id, user.userId, roleId);
    if (response !== undefined) {
      // Assigning the role is enough to update the list: the row reads `user.role` and the sort is
      // computed over it, so the member also moves to their new role's group. The list used to be
      // force-remounted with a :key bump on top of this, which threw away the DOM for no gain.
      user.role = newRole;
      useToast().success(`${user.name} was moved to ${newRole}`);
      // The role is saved, but their Speckle access may not match it (RTLCA-5531). Kept on screen
      // until dismissed: it says someone may still be able to publish, which is worth reading.
      if (response.speckleWarning) {
        useToast().warning(`${user.name}: ${response.speckleWarning}`, { timeout: false });
      }
    }
  } catch (error) {
    const fallback = `Something went wrong with updating the role for ${user.name}`;
    if (isAxiosError(error) && error.response?.status === 409) {
      // Refused before anything was written: they are the only owner of the project's Speckle model.
      // An expected answer with a reason to show, not a fault for Sentry. The role stays as it was.
      useToast().error(error.response?.data?.message ?? fallback);
    } else {
      Sentry.captureException(error);
      useToast().error(fallback);
    }
  } finally {
    pendingUserIds.value.delete(user.userId);
  }
};

const getUserImage = (userId: string) => {
  const foundUser = allUsers.value.find((user) => user.id === userId);

  return foundUser?.profilePictureUrl;
};

// One flat list, Admins first then Contributors then Viewers, replacing the three fixed-width
// columns (RTLCA-5155 layout revision) — a project's roles are rarely split evenly three ways, so
// a rigid three-column grid wasted width on whichever roles had few members while the busiest
// column did all the scrolling. Sort is stable, so within a role the existing fetch order holds.
const ROLE_SORT_ORDER = ["admin", "contributor", "viewer"];
// Anything the backend sends that isn't one of the three known roles sorts last rather than first —
// indexOf would return -1 and float it above Admin, which is the one position it definitely
// shouldn't occupy. It still renders (with its raw role as the label), just at the bottom.
const roleRank = (role: string) => {
  const rank = ROLE_SORT_ORDER.indexOf(role);
  return rank === -1 ? ROLE_SORT_ORDER.length : rank;
};
const sortedMembers = computed(() => [...usersAlreadyInProject.value].sort((a, b) => roleRank(a.role) - roleRank(b.role)));

// Both invite fields are required, so the button says so up front instead of accepting the click and
// answering with a toast that repeats what the two asterisks already state (RTLCA-5743). The toasts
// in `inviteUser` stay as the guard for a submit that arrives another way — the button is a view of
// this, not the only thing enforcing it.
const canInvite = computed(() => selectedUser.value !== undefined && selectedRole.value !== null);

const MEMBER_COLUMNS: Column[] = [
  { key: "member", label: "Member", align: "left" },
  { key: "role", label: "Role", align: "right" },
];
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Invite row: one plain line, not a bordered card — checking who already has access is
         what this is looked at for most of the time; inviting shouldn't outweigh it visually.
         Stacks below sm: at narrow widths the three fixed proportions squeezed the field down to
         a couple of visible characters. -->
    <div
      v-if="!readOnly"
      class="gap-px-12 flex flex-col sm:flex-row sm:items-end sm:gap-4">
      <div class="min-w-0 sm:flex-1">
        <SearchSelectUser
          :key="users.length"
          :users="users"
          :loaded="userListLoaded"
          data-cy="user-search"
          @selected-user="handleSelectedUser" />
      </div>
      <div class="sm:w-40">
        <SelectInputNew
          v-model="selectedRole"
          label="Role"
          placeholder="Select role"
          data-cy="role-select"
          :required="true">
          <option
            v-for="role in roles"
            :key="role"
            :value="role">
            {{ role }}
          </option>
        </SelectInputNew>
      </div>
      <div class="shrink-0">
        <!-- Natively disabled AND greyed: `disabled` alone leaves a primary-blue button that reads as
             ready, and the grey alone leaves it clickable. The two required fields carry the why —
             a tooltip couldn't, since a natively disabled button fires no mouse events. -->
        <Button
          :color="canInvite ? 'primary' : 'disabled'"
          :disabled="!canInvite"
          data-cy="invite-btn"
          :label="isInviting ? 'Inviting...' : 'Invite'"
          :is-loading="isInviting"
          @click="inviteUser" />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <p class="t-small-bold text-type-default">
        <!-- No count until the fetch lands, so this can't state "0 members" and then correct itself. -->
        {{ isLoadingMembers ? "Members" : `${sortedMembers.length} member${sortedMembers.length === 1 ? "" : "s"}` }}
      </p>

      <!-- A hairline, no shadow: this box sits ON its host's white ground — a dialog's body or a
           settings card — rather than above it, and inside the card a shadowed tile within a
           bordered card reads as two stacked surfaces. -->
      <div class="rounded-8 overflow-hidden border border-slate-200">
        <!-- The shared Table reusable rather than a hand-built list of rows, so the header band,
             row rules, hover and cell rhythm are the same ones every other table in the app gets.
             The last row's border is dropped because it would otherwise double up against the
             card's own bottom border. -->
        <Table
          :columns="MEMBER_COLUMNS"
          :rows="sortedMembers"
          :row-key="(row) => row.userId"
          :loading="isLoadingMembers"
          caption="Project members"
          table-class="[&_tbody_tr:last-child]:border-b-0">
          <template #cell-member="{ row }">
            <div class="gap-px-8 flex min-w-0 items-center">
              <MemberIdentity
                :name="row.name"
                :user-image="getUserImage(row.userId)"
                :is-self="user?.userId === row.userId" />
              <MemberSpeckleFlag
                v-if="row.speckleStatus === 'noAccount' || row.speckleStatus === 'notOnModel'"
                :status="row.speckleStatus"
                :is-self="user?.userId === row.userId" />
            </div>
          </template>

          <template #cell-role="{ row }">
            <div class="flex justify-end">
              <MemberRoleMenu
                :user="row"
                :self-is-contributer="selfIsContributor"
                :self-is-admin="selfIsAdmin"
                :is-self="user?.userId === row.userId"
                :is-pending="pendingUserIds.has(row.userId)"
                :read-only="readOnly"
                @remove="handleRemoveUser"
                @change-role="handleChangeRole" />
            </div>
          </template>

          <!-- A project always has at least one member, so an empty body means the fetch failed. -->
          <template #empty>
            {{ membersError ? "Could not load the member list. Reload the page to retry." : "No members yet" }}
          </template>
        </Table>
      </div>
    </div>
  </div>
</template>
